import { useQuery } from '@tanstack/react-query';
import type { MediaAsset, MediaProjectResponse, Quiz } from '../../types';
import {
  assetUrl,
  projectUrl,
  readJson,
  resolvePresentAddons,
} from '../addOnAssets';
import type { QuizAsset, QuizBank, QuizQuestion, UseQuizArgs } from './types';


/** The questions out of a raw `Quiz` asset. Already the wire shape the editor
 *  writes back, so this only supplies the empty default. */
export const parseQuiz = (quiz: Quiz | null): QuizQuestion[] =>
  quiz?.questions ?? [];

/** One key for the quiz read and the save's invalidation. */
export const quizKey = (apiBaseUrl: string, projectId: string) =>
  ['quiz', apiBaseUrl, projectId] as const;

/**
 * The Quiz read chain — the same three steps `useExperienceAddons` runs for
 * every add-on, for this one only:
 *
 *   project → storyboards[].add_ons[] → the Quiz asset_id
 *   asset   → signed path
 *   path    → `Quiz { questions: [{ question, answers }] }`
 *
 * Strict where the viewer is lenient: a bank that can't be read rejects
 * instead of reading as empty, so an editor never offers an empty quiz to be
 * saved over the real one.
 */
export const loadQuiz = async ({
  apiBaseUrl,
  projectId,
  token,
}: UseQuizArgs): Promise<QuizBank> => {
  const project = await readJson<MediaProjectResponse>(
    projectUrl(apiBaseUrl, projectId),
    token,
  );
  const assetId = resolvePresentAddons(project).get('Quiz')?.asset_id;
  if (!assetId) return { questions: [] };
  const asset = await readJson<MediaAsset>(
    assetUrl(apiBaseUrl, assetId),
    token,
  );
  const quiz = await readJson<Quiz>(asset.path);
  return { questions: parseQuiz(quiz), assetId };
};

/**
 * A project's Quiz questions on their own. Where `useExperienceAddons` loads
 * every add-on for the viewer, this reads just this one — for callers like the
 * experience edit page that want a single add-on's content, cached by React
 * Query across remounts. Needs a `QueryClientProvider` above it.
 */
export function useQuiz({
  apiBaseUrl,
  projectId,
  token,
}: UseQuizArgs): QuizAsset {
  const query = useQuery({
    queryKey: quizKey(apiBaseUrl, projectId),
    queryFn: () => loadQuiz({ apiBaseUrl, projectId, token }),
    enabled: Boolean(apiBaseUrl && projectId && token),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return {
    questions: query.data?.questions ?? [],
    assetId: query.data?.assetId,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}
